import { useMutation } from "convex/react";
import { X } from "lucide-react";
import { useState } from "react";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface EditCardDialogContentProps {
  cardId: Id<"cards">;
  initialQuestion?: string;
  initialAnswer?: string;
  initialSingleSided?: boolean;
}

export const EditCardDialogContent = (props: EditCardDialogContentProps) => {
  const [question, setQuestion] = useState(props.initialQuestion);
  const [answer, setAnswer] = useState(props.initialAnswer);
  const [singleSided, setSingleSided] = useState(props.initialSingleSided);

  const saveDisabled = singleSided ? !question : !question || !answer;

  const updateCardMutation = useMutation(api.cards.update);
  const saveCard = () =>
    updateCardMutation({
      id: props.cardId,
      question,
      answer,
      singleSided,
    });

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="text-xl">Edit Card</DialogTitle>
        <DialogDescription hidden={true}>
          Edit the card details below and click "Save" to update the card.
        </DialogDescription>
        <DialogClose className="absolute right-4 top-4 opacity-70 hover:opacity-100">
          <X className="h-4 w-4" />
        </DialogClose>
      </DialogHeader>
      <div className="space-y-4">
        <div className="border rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          <Textarea
            placeholder="Front of card"
            className={`border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none h-30`}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </div>
        {!singleSided && (
          <div className="border rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
            <Textarea
              placeholder="Back of card"
              className={`border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none h-30`}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </div>
        )}
        <div className="flex items-center justify-end space-x-2">
          <Checkbox
            id="single-sided"
            onClick={() => setSingleSided((prev) => !prev)}
            checked={singleSided}
          />
          <label htmlFor="single-sided">Single-sided</label>
        </div>
        <div className="flex justify-end space-x-2">
          <DialogClose asChild>
            <Button onClick={saveCard} disabled={saveDisabled}>
              SAVE
            </Button>
          </DialogClose>
        </div>
      </div>
    </DialogContent>
  );
};
